/**
 * Upload a finished policy.
 *
 * The product deliberately has no editor: hospitals write policies in Word or
 * Adobe and upload the result. So this page is a metadata form with a file on
 * it, and its job is to get the document into the workflow with enough
 * information attached that somebody can find it again.
 */

import { useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { ACCEPT, formatBytes, probeFile, rejectReason, useUploadPolicy, MAX_UPLOAD_BYTES } from './useUpload';
import styles from '../../shared/Page.module.css';
import own from './UploadPage.module.css';
import { SUMMARY_MAX, TITLE_MAX } from '../../types/fieldLimits';
import { CharCount } from '../../shared/CharCount';
import { FileDrop } from '../../shared/FileDrop';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { titleFromFileName } from './titleFromFileName';
import { attemptIdFor, rowKey } from './useBulkUpload';
import { ApprovalFields, ReviewAndAcknowledgementFields } from './ReviewAndApproval';
import { TaxonomyFields, type Audience } from './TaxonomyFields';
import { WhatHappensNext } from './WhatHappensNext';
import { useTaxonomy } from '../admin/useTaxonomy';
import { BulkUploadPage } from './BulkUploadPage';



/** The two ways in. Separate controls, same page — see the switch below. */
const MODES = [
  { value: 'single', label: 'One policy', description: 'A title, a summary and one PDF.' },
  // "Several at once" said how many, not what. This is still the same page and
  // the same queue; Import library (/admin/import) is the other thing entirely
  // — a spreadsheet and a zip, run by StandingDocs — and the two are not
  // merged.
  { value: 'bulk', label: 'Multiple policies', description: 'Choose or drop many files; each becomes a policy named from its file.' },
] as const;
type Mode = (typeof MODES)[number]['value'];

export function UploadPage() {
  const { session } = useAuth();
  const { stage, upload, reset } = useUploadPolicy(session?.orgId ?? null);

  // In the URL, so a reload and a shared link land on the same mode. A mode
  // switch is not a place you navigate back to, so it replaces the entry.
  const [params, setParams] = useSearchParams();
  const mode: Mode = params.get('mode') === 'bulk' ? 'bulk' : 'single';
  const setMode = (next: Mode) => {
    const p = new URLSearchParams(params);
    if (next === 'bulk') p.set('mode', 'bulk');
    else p.delete('mode');
    setParams(p, { replace: true });
  };

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [reviewCycle, setReviewCycle] = useState(12);
  const [requiresAck, setRequiresAck] = useState(true);
  // The header a surveyor reads. Optional at upload, editable on the record.
  const [departmentId, setDepartmentId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [audience, setAudience] = useState<Audience>('all');
  const departments = useTaxonomy('department', Boolean(session?.orgId));
  const categories = useTaxonomy('category', Boolean(session?.orgId));
  const docTypes = useTaxonomy('documentType', Boolean(session?.orgId));
  const [submitForReview, setSubmitForReview] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  /**
   * Whether the chosen file's bytes can actually be read. A File is a handle,
   * and a cloud placeholder is a handle to nothing — see probeFile.
   */
  const [check, setCheck] = useState<
    | { status: 'none' }
    | { status: 'checking' }
    | { status: 'ok'; bytes: Uint8Array }
    | { status: 'bad'; reason: string }
  >({ status: 'none' });
  /** Which probe is current, so a slow read of an earlier file cannot land
   *  after a later one was chosen. */
  const probeSeq = useRef(0);
  const [touched, setTouched] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  /** The last title guessed from a filename, so a second file can replace a
   *  guess but never something the person typed. */
  const derivedTitle = useRef<string | null>(null);

  const busy =
    stage.phase === 'creating' ||
    stage.phase === 'uploading' ||
    stage.phase === 'attaching' ||
    stage.phase === 'converting' ||
    stage.phase === 'submitting';

  /**
   * Clearing the department clears the audience with it. Without this the
   * department radio stays TICKED and becomes DISABLED, which reads as a
   * choice that has been made and then taken away; the submit already
   * coerces to 'all' in that state, so the control was the only thing
   * disagreeing with what would happen.
   */
  const chooseDepartment = (id: string) => {
    setDepartmentId(id);
    if (!id) setAudience('all');
  };

  /** Named in the audience choice and the summary, so neither says "the chosen department". */
  const departmentName = departments.data?.find((d) => d.id === departmentId)?.name;

  const titleError = touched && !title.trim() ? 'Give the policy a title.' : null;
  const typeError = touched && !documentTypeId ? 'Choose a document type.' : null;
  const fileError = touched
    ? (rejectReason(file) ?? (check.status === 'bad' ? check.reason : null))
    : null;

  function startOver() {
    derivedTitle.current = null;
    setTitle('');
    setSummary('');
    setFile(null);
    setCheck({ status: 'none' });
    setTouched(false);
    if (fileInput.current) fileInput.current.value = '';
    reset();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!title.trim() || !documentTypeId || rejectReason(file)) return;
    // Not until the bytes are in hand. While the probe runs the hint says
    // "Checking"; if it failed, fileError is already saying why.
    if (check.status !== 'ok') return;

    void upload({
      title: title.trim(),
      summary: summary.trim() || undefined,
      reviewCycleMonths: reviewCycle,
      requiresAcknowledgement: requiresAck,
      departmentIds: departmentId ? [departmentId] : undefined,
      categoryId: categoryId || undefined,
      documentTypeId: documentTypeId || undefined,
      targetAudience: departmentId && audience === 'departments' ? 'departments' : 'all',
      file: file!,
      bytes: check.bytes,
      submitForReview,
      /**
       * Stable for as long as this file is the one chosen, so pressing the
       * button again after a failed upload finds the policy the first attempt
       * created rather than creating a second (finding K).
       */
      attemptId: attemptIdFor(rowKey(file!)),
    });
  }

  if (stage.phase === 'done') {
    return (
      <div className={own.wrap}>
        <div className={styles.state} role="status">
          <h1 className={styles.stateTitle}>
            {submitForReview && !stage.note ? 'Submitted for approval' : 'Saved as a draft'}
          </h1>
          {/* The name on its own line: a policy title can be ninety
              characters, and inside a sentence that length fragments the
              sentence around it however wide the box is. */}
          <p className={styles.stateSubject}>{title}</p>
          <p className={styles.stateBody}>
            {stage.note
              ? stage.note
              : submitForReview
                ? 'It has gone into the approval workflow.'
                : 'It stays a draft until you submit it.'}
          </p>
          <div className={styles.actions} style={{ justifyContent: 'center' }}>
            <Link to={`/policy/${stage.policyId}`} className={`${styles.btn} ${styles.btnPrimary}`}>
              View the policy
            </Link>
            <button type="button" className={styles.btn} onClick={startOver}>
              Upload another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={own.wrap}>
      <h1 className={styles.title}>Upload a policy</h1>
      <p className={styles.subtitle}>
        Write the document in Word or Adobe, export it as a PDF, and upload it
        here. Everything after that — approval, publishing, acknowledgement —
        happens in StandingDocs.
      </p>

      {/*
        One switch, two controls. Bulk is not the single control with a
        `multiple` flag: it has a queue, per-file titles and per-file
        outcomes, and forcing those into a form built for one file would make
        both worse. The switch chooses which form is on the page.
      */}
      <div className={own.modeRow}>
        <SegmentedChoice
          legend="Upload"
          name="upload-mode"
          options={MODES}
          value={mode}
          onChange={setMode}
          disabled={busy}
        />
      </div>

      {mode === 'bulk' ? (
        <BulkUploadPage />
      ) : (
      <>
      {stage.phase === 'failed' && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-5)' }}>
          {stage.message}
        </div>
      )}

      <form onSubmit={onSubmit} noValidate className={own.form}>
        {/*
          TWO COLUMNS ON A DESKTOP, ONE EVERYWHERE ELSE.

          The left column is the document and the words on it, which is what
          somebody is actually doing; the right is how it will be filed and
          handled, which is a series of small decisions with defaults that are
          usually right. The grid collapses to one column below 1040px, in the
          order the sections are written, so a phone reads Policy first.
        */}
        <div className={own.columns}>
        <section className={own.panel} aria-labelledby="sec-policy">
        <h2 className={own.panelTitle} id="sec-policy">Policy</h2>
        <div className={own.field}>
          <label className={own.label} htmlFor="policy-title">
            Title
          </label>
          <input
            id="policy-title"
            maxLength={TITLE_MAX}
            className={own.input}
            value={title}
            // Clamped as well as maxLength'd. maxLength constrains TYPING;
            // a value arriving from autofill or a script is not typing, and
            // this field would then hold text the save is going to refuse.
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            disabled={busy}
            aria-invalid={Boolean(titleError)}
            aria-describedby={titleError ? 'policy-title-error' : undefined}
          />
          {titleError && (
            <p className={own.error} id="policy-title-error">{titleError}</p>
          )}
          <CharCount value={title} max={TITLE_MAX} />
        </div>

        <div className={own.field}>
          <label className={own.label} htmlFor="policy-summary">
            Summary <span className={own.optional}>optional</span>
          </label>
          <textarea
            id="policy-summary"
            maxLength={SUMMARY_MAX}
            className={own.textarea}
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value.slice(0, SUMMARY_MAX))}
            disabled={busy}
          />
          <CharCount value={summary} max={SUMMARY_MAX} />
          <p className={own.hint}>One or two sentences, shown in the list and on the policy page.</p>
        </div>

        <div className={own.field}>
          <label className={own.label} htmlFor="policy-file">
            Document
          </label>
          <FileDrop
            id="policy-file"
            inputRef={fileInput}
            accept={ACCEPT}
            disabled={busy}
            file={file}
            invalid={Boolean(fileError)}
            describedBy={fileError ? 'policy-file-error' : 'policy-file-hint'}
            className={own.dropTall}
            // NAME BOTH FORMATS WHERE PEOPLE ACTUALLY READ. "Choose a PDF"
            // was the only prominent word about the file, and Word was
            // mentioned in the hint underneath — so somebody with a .docx
            // could reasonably conclude it would be refused, export it, and
            // lose the editable original. `accept` has always taken both.
            prompt="Choose a PDF or Word document"
            // Choosing IS deciding, however it happens: a file that arrives
            // by picker or by drop gets checked at once, so an unacceptable
            // one explains itself before anybody presses Upload.
            onFile={(chosen) => {
              setFile(chosen);
              setTouched(true);

              // Read it now, not at submit. A placeholder from a cloud drive
              // fails here, in seconds, with advice — instead of at
              // "Uploading… 0%" forever, after a draft was already created.
              // Only the newest probe may report; an earlier slow one is
              // ignored.
              if (!rejectReason(chosen)) {
                const seq = ++probeSeq.current;
                setCheck({ status: 'checking' });
                void probeFile(chosen).then((result) => {
                  if (seq !== probeSeq.current) return;
                  setCheck(result.ok ? { status: 'ok', bytes: result.bytes } : { status: 'bad', reason: result.reason });
                });
              } else {
                setCheck({ status: 'none' });
              }

              // Guess the title from the file's name — but only into an empty
              // box, or over a guess the person has not edited. Something
              // they typed is theirs. A guess that comes back empty (a file
              // called "v2 (3).pdf") is no guess, and the box stays as it was.
              if (!title.trim() || title === derivedTitle.current) {
                const guess = titleFromFileName(chosen.name);
                if (guess) {
                  derivedTitle.current = guess;
                  setTitle(guess);
                }
              }
            }}
          />
          {fileError ? (
            <p className={own.error} id="policy-file-error">{fileError}</p>
          ) : (
            <p className={own.hint} id="policy-file-hint" aria-live="polite">
              PDF or Word (.docx), up to {formatBytes(MAX_UPLOAD_BYTES)}. A Word file is kept as the original and converted to PDF for reading.
              {file && check.status === 'checking' && ` Checking ${file.name}…`}
              {file && check.status === 'ok' && ` Selected: ${file.name} (${formatBytes(file.size)})`}
            </p>
          )}
        </div>

        </section>

        <div className={own.side}>
          <section className={own.panel} aria-labelledby="sec-classification">
            <h2 className={own.panelTitle} id="sec-classification">Classification</h2>
            <TaxonomyFields
              departments={departments.data}
              categories={categories.data}
              docTypes={docTypes.data}
              departmentId={departmentId}
              onDepartmentId={chooseDepartment}
              categoryId={categoryId}
              onCategoryId={setCategoryId}
              documentTypeId={documentTypeId}
              onDocumentTypeId={setDocumentTypeId}
              documentTypeError={typeError}
              disabled={busy}
            />
          </section>

          <section className={own.panel} aria-labelledby="sec-review">
            <h2 className={own.panelTitle} id="sec-review">Review &amp; acknowledgement</h2>
            <ReviewAndAcknowledgementFields
              reviewCycle={reviewCycle}
              onReviewCycle={setReviewCycle}
              requiresAck={requiresAck}
              onRequiresAck={setRequiresAck}
              audience={audience}
              onAudience={setAudience}
              departmentId={departmentId}
              departmentName={departmentName}
              disabled={busy}
            />
          </section>

          <section className={own.panel} aria-labelledby="sec-approval">
            <h2 className={own.panelTitle} id="sec-approval">Approval</h2>
            <ApprovalFields
              submitForReview={submitForReview}
              onSubmitForReview={setSubmitForReview}
              disabled={busy}
            />
          </section>

          <WhatHappensNext
            reviewCycle={reviewCycle}
            requiresAck={requiresAck}
            audience={audience}
            departmentId={departmentId}
            departmentName={departmentName}
            documentTypeId={documentTypeId}
            submitForReview={submitForReview}
          />
        </div>
        </div>

        {busy && (
          <div className={own.progress} role="status" aria-live="polite">
            {stage.phase === 'uploading' ? (
              <>
                <div className={own.progressLabel}>
                  Uploading… <span className="tnum">{stage.percent}%</span>
                </div>
                <div
                  className={own.track}
                  role="progressbar"
                  aria-valuenow={stage.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Upload progress"
                >
                  <div className={own.fill} style={{ width: `${stage.percent}%` }} />
                </div>
              </>
            ) : (
              <div className={own.progressLabel}>
                {stage.phase === 'creating' && 'Creating the policy…'}
                {stage.phase === 'attaching' && 'Attaching the document…'}
                {stage.phase === 'converting' && 'Converting the Word document to PDF…'}
                {stage.phase === 'submitting' && 'Submitting for approval…'}
              </div>
            )}
          </div>
        )}

        <div className={`${styles.actions} ${own.footer}`}>
          <button
            type="submit"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy}
          >
            {busy ? 'Working…' : submitForReview ? 'Upload and submit for approval' : 'Upload as draft'}
          </button>
          <Link to="/policies" className={styles.btn}>Cancel</Link>
        </div>
      </form>
      </>
      )}
    </div>
  );
}
